<template>
  <NumeraiModal
    v-if="model"
    ref="modal"
    :dialog="dialog"
    :button="button"
    title="Compute webhook"
    save-button-text="Register"
    :show-footer="false"
    :fields="fields"
    :fill="fill"
    :full-width="full"
    :disabled="fetching"
    @close="close"
  >
    <template v-slot:activator>
      <slot></slot>
    </template>
    <span
      >Copy your submission webhook from the 'nodes.json' file in '.numerai'
      folder in your home directory. Follow the
      <a
        href="https://github.com/numerai/numerai-cli"
        target="_blank"
        rel="noopener noreferrer"
        >docs</a
      >
      if you are lost.
    </span>
    <v-text-field
      v-model="fields.newSubmissionWebhook"
      label="Submission Webhook"
      outlined
      color="#424242"
      hide-details
      autofocus
      class="mt-6 mb-8"
      :disabled="!editing"
      :aria-multiline="true"
    >
    </v-text-field>
    <span class="mb-8 status">{{ msg }}</span>
    <div v-if="showDailyCompute" class="mb-4">
      <label>Daily Triggers</label>
      <ComputeLiteDailyToggle
        class="ml-2"
        :model-id="model.id"
        :model-name="model.name"
        :daily-triggers-enabled="model.isComputeWeekdayEnabled"
      ></ComputeLiteDailyToggle>
    </div>
    <div class="buttons-container pt-2">
      <slot name="footer">
        <button v-if="editing" class="numerai-button mr-4" @click="register">
          Register
        </button>
        <button
          v-else
          class="numerai-button mr-4"
          @click="() => (editing = true)"
        >
          Edit
        </button>
        <span
          v-if="
            model.submissionWebhook &&
              model.submissionWebhook == fields.newSubmissionWebhook
          "
          class="mr-4"
        >
          <button
            v-if="!testing"
            class="numerai-button test-button"
            @click="testWebhook"
          >
            Test
          </button>
          <numerai-loader
            v-else
            :width="40"
            :full-width="false"
            class="loading"
          />
        </span>
        <button
          v-if="model.submissionWebhook"
          class="numerai-button button-primary"
          @click="deregister"
        >
          Remove
        </button>
        <button
          class="numerai-button button-tertiary close-button"
          @click="close"
        >
          Close
        </button>
      </slot>
    </div>
  </NumeraiModal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import NumeraiModal from '@/components/shared/NumeraiModal'
import NumeraiLoader from '@/components/shared/NumeraiLoader'
import ComputeLiteDailyToggle from '@/components/shared/ComputeLiteDailyToggle'

export default {
  components: {
    NumeraiModal,
    NumeraiLoader,
    ComputeLiteDailyToggle,
  },
  props: {
    fill: { type: Boolean, default: false },
    full: { type: Boolean, default: false },
    buttonText: { type: String, default: '' },
    model: { type: Object, default: null },
    dialog: { type: Boolean, default: false },
    button: { type: Boolean, default: true },
    onClose: { type: Function, default: null },
    onSetWebhook: { type: Function, default: null },
  },
  data() {
    return {
      fields: {
        newSubmissionWebhook: this.model && this.model.submissionWebhook,
        modelId: this.model && this.model.id,
      },
      msg: '',
      fetching: false,
      testing: false,
      editing: this.model && !this.model.submissionWebhook,
      showDailyCompute: false,
    }
  },
  computed: {
    ...mapGetters(['isLoggedIn']),
  },
  watch: {
    model(newModel) {
      this.fields.newSubmissionWebhook = newModel && newModel.submissionWebhook
      this.fields.modelId = newModel && newModel.id
      this.editing = newModel && !newModel.submissionWebhook
      this.showDailyCompute = newModel && newModel.computeEnabled
    },
  },
  created() {
    this.showDailyCompute = this.model && this.model.computeEnabled
  },
  methods: {
    ...mapActions(['showSnackBar']),
    validUrl(str) {
      const pattern = new RegExp(
        '^(https?:\\/\\/)?' + // protocol
        '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
        '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
        '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
        '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
          '(\\#[-a-z\\d_]*)?$',
        'i',
      ) // fragment locator
      return !!pattern.test(str)
    },
    validateSubmissionUrl() {
      if (this.fields.newSubmissionWebhook === null) {
        return true
      } else if (this.fields.newSubmissionWebhook.s === '') {
        this.msg = 'URL cannot be blank, use "Remove" to delete the webhook.'
        return false
      } else if (this.validUrl(this.fields.newSubmissionWebhook) === false) {
        this.msg = 'URL Format Incorrect'
        return false
      } else {
        this.msg = null
        return true
      }
    },
    async register() {
      this.fetching = true
      this.msg = ''
      try {
        if (this.validateSubmissionUrl(this.fields.newSubmissionWebhook)) {
          await this.$client.changeSubmissionWebhook(this.fields)
          this.model.submissionWebhook = this.fields.newSubmissionWebhook
          this.msg = 'Your webhook has been set.'
          if (this.fields.newSubmissionWebhook != null) {
            this.showDailyCompute = true
          } else {
            this.showDailyCompute = false
          }
        }
      } catch (e) {
        this.msg = e.message
      } finally {
        this.onSetWebhook && this.onSetWebhook()
      }
      this.fetching = false
    },
    async deregister() {
      this.fields.newSubmissionWebhook = null
      this.register()
      this.editing = true
    },
    close() {
      this.msg = ''
      this.$refs.modal.close()
      this.onClose && this.onClose()
    },
    async testWebhook() {
      this.testing = true
      this.msg = ''

      if (!this.validateSubmissionUrl()) {
        return
      }

      try {
        await this.$client.triggerModelWebhook({
          modelId: this.model.id,
        })
        this.msg = `Your webhook is tested and working!`
      } catch (e) {
        this.msg = e.message
      }
      this.testing = false
    },
  },
}
</script>

<style scoped>
.buttons-container {
  display: flex;
}
.test-button {
  background-color: var(--color-happy-green);
}
.test-button:hover {
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.363);
}
.close-button {
  margin-left: auto;
}
.loading {
  height: 40px;
  padding: 0;
  margin: 0;
  display: flex;
  flex-shrink: 1;
}
.model-container {
  background-color: var(--color-very-subtle);
  border-radius: var(--radius);
  padding: 16px;
}
.head-row {
  justify-content: space-between;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--color-tertiary);
}
.head {
  font-family: var(--brand-font);
  text-transform: uppercase;
  font-weight: 400;
}
.model-row {
  justify-content: space-between;
  padding: 10px 0px;
  border-bottom: 1px solid var(--color-subtle);
}
.model-row:last-child {
  border-bottom: 0px;
}
.model-row:hover {
  background-color: #6666660e;
}
.model {
  text-transform: uppercase;
}
.status {
  white-space: pre-line;
}
</style>
